<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="visible" class="mec-backdrop" @mousedown.self="cerrar">
        <div class="mec-panel" role="dialog" aria-modal="true">

          <!-- Cabecera -->
          <div class="mec-cabecera">
            <span><i class="bi bi-person-gear me-2"></i>Editar datos del cliente</span>
            <button class="mec-cerrar" @click="cerrar" title="Cerrar">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Cuerpo -->
          <div class="mec-cuerpo">

            <!-- Cargando -->
            <div v-if="cargando" class="mec-cargando">
              <div class="spinner-border" style="color:var(--primary-main)"></div>
              <span>Cargando datos del cliente...</span>
            </div>

            <!-- Error de carga -->
            <div v-else-if="errorCarga" class="mec-alerta mec-alerta--error">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ errorCarga }}
            </div>

            <!-- Formulario -->
            <form v-else @submit.prevent="guardar" novalidate>

              <!-- Alerta de resultado -->
              <div v-if="alerta.mensaje" class="mec-alerta" :class="'mec-alerta--' + alerta.tipo">
                <i :class="{
                  'bi bi-check-circle-fill': alerta.tipo === 'exito',
                  'bi bi-exclamation-triangle-fill': alerta.tipo === 'error'
                }" class="me-2"></i>
                {{ alerta.mensaje }}
              </div>

              <div class="mec-form-grid">

                <!-- Tipo de identificación -->
                <div class="mec-campo">
                  <label class="mec-etiqueta">Tipo de identificación <span class="mec-req">*</span></label>
                  <select v-model="form.tipo_identificacion" class="mec-select" required>
                    <option value="">Seleccione</option>
                    <option value="04">Cédula de identidad</option>
                    <option value="05">Pasaporte</option>
                    <option value="06">RUC</option>
                    <option value="07">Consumidor final</option>
                    <option value="08">Identificación del exterior</option>
                  </select>
                </div>

                <!-- Número de identificación -->
                <div class="mec-campo">
                  <label class="mec-etiqueta">Número de identificación <span class="mec-req">*</span></label>
                  <input
                    v-model="form.numero_identificacion"
                    type="text"
                    class="mec-input"
                    placeholder="Ej: 0912345678001"
                    required
                    maxlength="20"
                  />
                </div>

                <!-- Razón social / Nombre -->
                <div class="mec-campo mec-campo--full">
                  <label class="mec-etiqueta">Razón social / Nombre completo <span class="mec-req">*</span></label>
                  <input
                    v-model="form.razon_social"
                    type="text"
                    class="mec-input"
                    placeholder="Ej: Juan Pérez García"
                    required
                    maxlength="300"
                  />
                </div>

                <!-- Dirección -->
                <div class="mec-campo mec-campo--full">
                  <label class="mec-etiqueta">Dirección</label>
                  <input
                    v-model="form.direccion"
                    type="text"
                    class="mec-input"
                    placeholder="Ej: Av. Principal 123 y Calle 2"
                    maxlength="300"
                  />
                </div>

                <!-- Teléfono -->
                <div class="mec-campo">
                  <label class="mec-etiqueta">Teléfono</label>
                  <input
                    v-model="form.telefono"
                    type="text"
                    class="mec-input"
                    placeholder="Ej: 0998765432"
                    maxlength="20"
                  />
                </div>

                <!-- Correo electrónico -->
                <div class="mec-campo">
                  <label class="mec-etiqueta">Correo electrónico</label>
                  <input
                    v-model="form.correo"
                    type="email"
                    class="mec-input"
                    placeholder="correo@empresa.com"
                    maxlength="100"
                  />
                </div>

              </div><!-- /form-grid -->

              <!-- Acciones -->
              <div class="mec-acciones">
                <button type="button" class="mec-btn mec-btn--cancelar" @click="cerrar" :disabled="guardando">
                  Cancelar
                </button>
                <button type="submit" class="mec-btn mec-btn--guardar" :disabled="guardando">
                  <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-check-circle me-1"></i>
                  Guardar cambios
                </button>
              </div>

            </form>
          </div><!-- /cuerpo -->

        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import api from '@/services/api'
import notificaciones from '@/services/notificationService.js'

export default {
  name: 'ModalEditarCliente',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    clienteId: {
      type: [Number, String, null],
      default: null,
    },
  },

  emits: ['cerrar', 'guardado'],

  data() {
    return {
      cargando:   false,
      guardando:  false,
      errorCarga: '',
      alerta:     { mensaje: '', tipo: 'exito' },
      form: {
        tipo_identificacion:  '',
        numero_identificacion: '',
        razon_social:          '',
        direccion:             '',
        telefono:              '',
        correo:                '',
      },
    }
  },

  watch: {
    // Cargar datos cada vez que se abre el modal con un clienteId válido
    visible(nuevoValor) {
      if (nuevoValor && this.clienteId) {
        this.alerta = { mensaje: '', tipo: 'exito' }
        this.cargarCliente()
      }
    },
    clienteId(nuevoId) {
      if (nuevoId && this.visible) {
        this.alerta = { mensaje: '', tipo: 'exito' }
        this.cargarCliente()
      }
    },
  },

  methods: {
    async cargarCliente() {
      if (!this.clienteId) return
      this.cargando   = true
      this.errorCarga = ''
      try {
        const resp = await api.get('/clientes/' + this.clienteId)
        const c    = resp.data?.data || resp.data
        this.form = {
          tipo_identificacion:   c.tipo_identificacion                           || '',
          numero_identificacion: c.identification_number || c.identificacion_cliente || '',
          razon_social:          c.business_name         || c.razon_social_cliente   || '',
          direccion:             c.address               || c.direccion_cliente       || '',
          telefono:              c.phone                 || c.telefono_cliente        || '',
          correo:                c.email                 || c.correo_cliente          || '',
        }
      } catch (e) {
        this.errorCarga = 'No se pudieron cargar los datos del cliente.'
      } finally {
        this.cargando = false
      }
    },

    async guardar() {
      this.alerta = { mensaje: '', tipo: 'exito' }

      if (!this.form.tipo_identificacion) {
        this.alerta = { mensaje: 'El tipo de identificación es obligatorio.', tipo: 'error' }
        return
      }
      if (!this.form.numero_identificacion?.trim()) {
        this.alerta = { mensaje: 'El número de identificación es obligatorio.', tipo: 'error' }
        return
      }
      if (!this.form.razon_social?.trim()) {
        this.alerta = { mensaje: 'La razón social o nombre es obligatorio.', tipo: 'error' }
        return
      }

      this.guardando = true
      try {
        // Mapeo de campos internos (español) al formato que espera la API (legacy)
        await api.put('/clientes/' + this.clienteId, {
          tipo_identificacion:   this.form.tipo_identificacion,
          identification_number: this.form.numero_identificacion.trim(),
          business_name:         this.form.razon_social.trim(),
          address:               this.form.direccion?.trim() || null,
          phone:                 this.form.telefono?.trim()  || null,
          email:                 this.form.correo?.trim()    || null,
        })
        notificaciones.success('Datos del cliente actualizados.')
        this.$emit('guardado', { ...this.form, id: this.clienteId })
      } catch (e) {
        const msg = e?.response?.data?.error || 'Error al actualizar el cliente.'
        this.alerta = { mensaje: msg, tipo: 'error' }
      } finally {
        this.guardando = false
      }
    },

    cerrar() {
      if (this.guardando) return
      this.$emit('cerrar')
    },
  },
}
</script>

<style scoped>
/* ══════════════════════════════════════════════════════════════
   BACKDROP
══════════════════════════════════════════════════════════════ */
.mec-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

/* ══════════════════════════════════════════════════════════════
   PANEL
══════════════════════════════════════════════════════════════ */
.mec-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: .6rem;
  box-shadow: var(--shadow-md);
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  overflow-y: auto;
}

/* ══════════════════════════════════════════════════════════════
   CABECERA
══════════════════════════════════════════════════════════════ */
.mec-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: .7rem 1rem;
  background: var(--primary-main);
  color: #fff;
  font-size: .9rem;
  font-weight: 600;
  border-radius: .6rem .6rem 0 0;
}
.mec-cerrar {
  background: none;
  border: none;
  color: rgba(255,255,255,.8);
  cursor: pointer;
  padding: .2rem .4rem;
  border-radius: .3rem;
  transition: background .12s;
}
.mec-cerrar:hover { background: rgba(255,255,255,.2); color: #fff; }

/* ══════════════════════════════════════════════════════════════
   CUERPO
══════════════════════════════════════════════════════════════ */
.mec-cuerpo {
  padding: 1rem;
}

.mec-cargando {
  display: flex;
  align-items: center;
  gap: .75rem;
  justify-content: center;
  padding: 2rem 0;
  color: var(--text-secondary);
  font-size: .9rem;
}

/* ══════════════════════════════════════════════════════════════
   ALERTA
══════════════════════════════════════════════════════════════ */
.mec-alerta {
  padding: .55rem .85rem;
  border-radius: .4rem;
  font-size: .83rem;
  margin-bottom: .85rem;
  border: 1px solid transparent;
}
.mec-alerta--error {
  background: rgba(220,53,69,.1);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}
.mec-alerta--exito {
  background: rgba(25,135,84,.1);
  border-color: var(--accent-success);
  color: var(--accent-success);
}

/* ══════════════════════════════════════════════════════════════
   FORMULARIO
══════════════════════════════════════════════════════════════ */
.mec-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .75rem;
  margin-bottom: 1rem;
}

.mec-campo {
  display: flex;
  flex-direction: column;
  gap: .25rem;
}
.mec-campo--full {
  grid-column: 1 / -1;
}

.mec-etiqueta {
  font-size: .79rem;
  color: var(--text-secondary);
  font-weight: 500;
}
.mec-req {
  color: var(--accent-danger);
}

.mec-input,
.mec-select {
  background: var(--bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: .375rem;
  padding: .4rem .65rem;
  font-size: .85rem;
  width: 100%;
  outline: none;
  transition: border-color .15s;
}
.mec-input:focus,
.mec-select:focus {
  border-color: var(--border-focus);
}

/* ══════════════════════════════════════════════════════════════
   ACCIONES
══════════════════════════════════════════════════════════════ */
.mec-acciones {
  display: flex;
  gap: .6rem;
  justify-content: flex-end;
  padding-top: .5rem;
  border-top: 1px solid var(--border-main);
}

.mec-btn {
  padding: .45rem 1rem;
  font-size: .84rem;
  font-weight: 500;
  border-radius: .375rem;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  transition: background .15s, opacity .15s;
}
.mec-btn:disabled { opacity: .55; cursor: not-allowed; }
.mec-btn--cancelar {
  background: transparent;
  border: 1px solid var(--border-main);
  color: var(--text-secondary);
}
.mec-btn--cancelar:hover:not(:disabled) { background: var(--border-main); }
.mec-btn--guardar {
  background: var(--primary-main);
  color: #fff;
}
.mec-btn--guardar:hover:not(:disabled) { background: var(--primary-hover); }

/* ══════════════════════════════════════════════════════════════
   TRANSICIÓN
══════════════════════════════════════════════════════════════ */
.modal-fade-enter-active,
.modal-fade-leave-active { transition: opacity .2s ease; }
.modal-fade-enter-from,
.modal-fade-leave-to   { opacity: 0; }
</style>
